import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/CoursePage.vue'),
    meta: {
      title: '課程招商',
      transition: 'fade'
    }
  },
  {
    path: '/courses',
    name: 'CourseList',
    component: () => import('@/views/CourseListPage.vue'),
    meta: {
      title: '課程中心',
      transition: 'fade'
    }
  },
  {
    path: '/course/:id',
    name: 'CourseDetail',
    component: () => import('@/views/CoursePage.vue'),
    meta: {
      title: '課程詳情',
      transition: 'fade'
    }
  },
  {
    path: '/checkin',
    name: 'Checkin',
    component: () => import('@/views/CheckinPage.vue'),
    meta: {
      title: '签到',
      transition: 'slide-up'
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/AdminPanel.vue'),
    meta: {
      title: '管理后台',
      requiresAuth: true,
      transition: 'slide-left'
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/components/admin/Dashboard.vue'),
        meta: {
          title: '儀表板'
        }
      },
      {
        path: 'courses',
        name: 'AdminCourses',
        component: () => import('@/components/admin/ContentEditor.vue'),
        meta: {
          title: '課程管理'
        }
      },
      {
        path: 'students',
        name: 'AdminStudents',
        component: () => import('@/components/admin/StudentList.vue'),
        meta: {
          title: '學員管理'
        }
      },
      {
        path: 'checkin',
        name: 'AdminCheckin',
        component: () => import('@/components/admin/CheckinManager.vue'),
        meta: {
          title: '簽到管理'
        }
      },
      {
        path: 'analytics',
        name: 'AdminAnalytics',
        component: () => import('@/components/admin/Analytics.vue'),
        meta: {
          title: '數據分析'
        }
      },
      {
        path: 'stores',
        name: 'AdminStores',
        component: () => import('@/components/admin/StoreManager.vue'),
        meta: {
          title: '租戶管理',
          requiresSuperuser: true
        }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/components/admin/UserManager.vue'),
        meta: {
          title: '用戶管理'
        }
      },
      {
        path: 'my-store',
        name: 'AdminMyStore',
        component: () => import('@/components/admin/MyStore.vue'),
        meta: {
          title: '我的租戶'
        }
      },
      {
        path: 'banner',
        name: 'AdminBanner',
        component: () => import('@/components/admin/BannerEditor.vue'),
        meta: {
          title: '海報管理'
        }
      },
      {
        path: 'videos',
        name: 'AdminVideos',
        component: () => import('@/components/admin/VideoManager.vue'),
        meta: {
          title: '視頻管理'
        }
      },
      {
        path: 'page-config',
        name: 'AdminPageConfig',
        component: () => import('@/components/admin/PageConfigEditor.vue'),
        meta: {
          title: '頁面配置'
        }
      },
      {
        path: 'system-config',
        name: 'AdminSystemConfig',
        component: () => import('@/components/admin/SystemConfig.vue'),
        meta: {
          title: '系統配置'
        }
      }
    ]
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: {
      title: '登录',
      hideForAuth: true,
      transition: 'fade'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundPage.vue'),
    meta: {
      title: '页面未找到'
    }
  }
]

// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0 }
    }
  }
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // 处理 C 端租户参数
  const storeCode = to.query.store as string
  if (storeCode && !authStore.isAuthenticated) {
    await authStore.loadStoreByCode(storeCode)
  }
  
  // 动态导入 config store 并设置页面标题
  if (to.meta.title) {
    const { useConfigStore } = await import('@/stores/config')
    const configStore = useConfigStore()
    
    // 如果配置未載入，先載入
    if (!configStore.loaded) {
      await configStore.loadConfig(storeCode)
    }
    
    const siteName = configStore.siteName || '課程報名系統'
    document.title = `${siteName}`
  }
  
  // 检查认证状态
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }
  
  // 检查超级管理员权限
  if (to.meta.requiresSuperuser && !authStore.isSuperuser) {
    next({ name: 'AdminDashboard' })
    return
  }
  
  // 已登录用户访问登录页面时重定向
  if (to.meta.hideForAuth && authStore.isAuthenticated) {
    next({ name: 'AdminDashboard' })
    return
  }
  
  next()
})

export default router